import axios from 'axios';
import { Topic, TopicSubmission, RatingSubmission, Team, ApiResponse } from '../types/index.ts';
import { authService } from './auth.ts';

// Use hostname to determine environment
const isProduction = window.location.hostname.includes('cloudfront.net')
  || window.location.hostname.includes('sharebrief.startups.aws.dev');
const API_BASE_URL = isProduction
  ? 'https://cy7v0la0lk.execute-api.us-east-2.amazonaws.com/prod'
  : 'http://localhost:8000/api';

console.log('API Configuration:', { hostname: window.location.hostname, isProduction, API_BASE_URL });

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add JWT token to all requests
api.interceptors.request.use((config) => {
  const token = authService.getAccessToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 responses and retry with refreshed token
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        await authService.refreshTokens();
        const token = authService.getAccessToken();
        if (token) {
          originalRequest.headers.Authorization = `Bearer ${token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Token refresh failed, redirect to login
        authService.redirectToLogin();
        return Promise.reject(error);
      }
    }
    
    return Promise.reject(error);
  }
);

// Helper function to organize topics by session type
const organizeTopicsBySession = (topics: any[]): { next: Topic[], future: Topic[], past: Topic[] } => {
  console.log('organizeTopicsBySession - raw topics:', topics.map(t => ({ 
    id: t.id, 
    title: t.title, 
    presenters: t.presenters 
  })));
  
  const next: Topic[] = [];
  const future: Topic[] = [];
  const past: Topic[] = [];
  
  topics.forEach(topic => {
    const mappedTopic: Topic = {
      topicId: topic.id,
      title: topic.title,
      description: topic.description,
      presenters: topic.presenters || [topic.submittedBy], // Fallback to submittedBy if no presenters
      recordingUrl: topic.recordingUrl,
      builderSpaceUrl: topic.builderSpaceUrl || '',
      gitlabUrl: topic.gitlabUrl || '',
      geoRegion: topic.geoRegion,
      sessionType: topic.sessionType || 'future',
      teamId: topic.teamId,
      attendeeCount: topic.attendeeCount || 0,
      sessionCompleted: false,
      createdAt: topic.submittedAt,
      duration: topic.duration,
      estimatedPresentationDate: topic.estimatedPresentationDate,
      averageUsefulnessRating: topic.averageUsefulnessRating,
      totalCustomerConversations: topic.totalCustomerConversations,
      totalHoursSaved: topic.totalHoursSaved
    };

    if (mappedTopic.sessionType === 'next') {
      next.push(mappedTopic);
    } else if (mappedTopic.sessionType === 'future') {
      future.push(mappedTopic);
    } else {
      past.push(mappedTopic);
    }
  });

  // Sort future topics: dated topics first (ascending by date), then undated topics
  // Ties broken by createdAt ascending (oldest submission first)
  future.sort((a, b) => {
    const aDate = a.estimatedPresentationDate;
    const bDate = b.estimatedPresentationDate;

    // Both have dates: sort by date ascending
    if (aDate && bDate) {
      if (aDate !== bDate) return aDate.localeCompare(bDate);
      return (a.createdAt || '').localeCompare(b.createdAt || '');
    }
    // Only a has date: a comes first
    if (aDate && !bDate) return -1;
    // Only b has date: b comes first
    if (!aDate && bDate) return 1;
    // Neither has date: sort by createdAt ascending
    return (a.createdAt || '').localeCompare(b.createdAt || '');
  });
  
  return { next, future, past };
};

export const topicApi = {
  // Get teams
  getTeams: async (): Promise<Team[]> => {
    const response = await api.get('/teams');
    return response.data;
  },

  // Create team
  createTeam: async (teamName: string): Promise<ApiResponse<Team>> => {
    try {
      const response = await api.post('/teams', { teamName });
      return { success: true, data: response.data };
    } catch (error: any) {
      if (error.response?.status === 409 || (error.response?.status === 400 && error.response?.data?.error?.includes('already exists'))) {
        return { success: false, error: 'A series with that name already exists. Please choose a different name.' };
      }
      return { success: false, error: error.response?.data?.error || 'Failed to create series' };
    }
  },

  // Update team name
  updateTeam: async (teamId: string, teamName: string): Promise<ApiResponse<Team>> => {
    try {
      const response = await api.put(`/teams/${teamId}`, { teamName });
      return { success: true, data: response.data };
    } catch (error: any) {
      if (error.response?.status === 409 || (error.response?.status === 400 && error.response?.data?.error?.includes('already exists'))) {
        return { success: false, error: 'A series with that name already exists. Please choose a different name.' };
      }
      return { success: false, error: error.response?.data?.error || 'Failed to update series name' };
    }
  },

  // Delete team and all associated data
  deleteTeam: async (teamId: string): Promise<ApiResponse<any>> => {
    try {
      const response = await api.delete(`/teams/${teamId}`);
      return { success: true, data: response.data };
    } catch (error: any) {
      if (error.response?.status === 404) {
        return { success: false, error: 'Team not found' };
      }
      return { success: false, error: error.response?.data?.error || 'Failed to delete series' };
    }
  },

  // Get topics organized by session type for a team
  getTopicsBySession: async (teamId: string): Promise<{ next: Topic[], future: Topic[], past: Topic[] }> => {
    const response = await api.get('/topics', { params: { teamId } });
    return organizeTopicsBySession(response.data);
  },

  // Get topic by ID
  getTopicById: async (topicId: string): Promise<Topic | null> => {
    try {
      const response = await api.get(`/topics/${topicId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  // Submit new topic
  submitTopic: async (topic: TopicSubmission): Promise<ApiResponse<Topic>> => {
    const payload: any = {
      title: topic.title,
      description: topic.description,
      submittedBy: 'ryanmasc', // System user who submitted
      presenterAlias: topic.presenterAlias, // Actual presenter
      sessionType: topic.sessionType,
      teamId: topic.teamId
    };
    
    // Include duration if provided
    if (topic.duration !== undefined) {
      payload.duration = topic.duration;
    }

    // Include geoRegion if provided
    if (topic.geoRegion) {
      payload.geoRegion = topic.geoRegion;
    }

    // Include URL fields if provided
    if ((topic as any).recordingUrl) {
      payload.recordingUrl = (topic as any).recordingUrl;
    }
    if ((topic as any).builderSpaceUrl) {
      payload.builderSpaceUrl = (topic as any).builderSpaceUrl;
    }
    if ((topic as any).gitlabUrl) {
      payload.gitlabUrl = (topic as any).gitlabUrl;
    }
    
    // Include presentedAt if provided (for topics marked as already presented)
    if ((topic as any).presentedAt) {
      payload.presentedAt = (topic as any).presentedAt;
    }

    // Include estimatedPresentationDate if provided
    if (topic.estimatedPresentationDate) {
      payload.estimatedPresentationDate = topic.estimatedPresentationDate;
    }
    
    const response = await api.post('/topics', payload);
    return { success: true, data: response.data };
  },

  // Update topic
  updateTopic: async (topicId: string, updates: any): Promise<ApiResponse<Topic>> => {
    const payload: any = {
      title: updates.title,
      description: updates.description,
      presenterAlias: updates.presenterAlias,
      geoRegion: updates.geoRegion,
      recordingUrl: updates.recordingUrl,
      builderSpaceUrl: updates.builderSpaceUrl,
      gitlabUrl: updates.gitlabUrl,
      sessionType: updates.sessionType
    };
    
    // Include duration if provided
    if (updates.duration !== undefined) {
      payload.duration = updates.duration;
    }
    
    // Include presentedAt if provided
    if (updates.presentedAt) {
      payload.presentedAt = updates.presentedAt;
    }

    // Include estimatedPresentationDate if explicitly provided (value to set, null to clear)
    if ('estimatedPresentationDate' in updates) {
      payload.estimatedPresentationDate = updates.estimatedPresentationDate;
    }
    
    const response = await api.put(`/topics/${topicId}`, payload);
    return { success: true, data: response.data };
  },

  // Delete topic
  deleteTopic: async (topicId: string): Promise<ApiResponse<any>> => {
    const response = await api.delete(`/topics/${topicId}`);
    return { success: true, data: response.data };
  },

  // Submit rating
  submitRating: async (topicId: string, rating: RatingSubmission): Promise<ApiResponse<any>> => {
    const response = await api.post('/ratings', {
      topicId,
      userId: rating.userAlias,
      usefulnessRating: rating.usefulnessRating,
      recommendationRating: rating.recommendationRating,
      customerConversations: rating.customerConversations,
      hoursSaved: rating.hoursSaved
    });
    return { success: true, data: response.data };
  },

  // Update existing rating
  updateRating: async (topicId: string, rating: RatingSubmission): Promise<ApiResponse<any>> => {
    return topicApi.submitRating(topicId, rating);
  },

  // Delete rating
  deleteRating: async (topicId: string, userId: string): Promise<ApiResponse<any>> => {
    const response = await api.delete(`/ratings/${topicId}/${userId}`);
    return { success: true, data: response.data };
  },

  // Get ratings for a topic
  getRatings: async (topicId: string): Promise<any[]> => {
    const response = await api.get(`/topics/${topicId}/ratings`);
    return response.data;
  },

  // Update attendee count
  updateAttendeeCount: async (topicId: string, count: number): Promise<ApiResponse<any>> => {
    const response = await api.put(`/topics/${topicId}/attendees`, { count });
    return { success: true, data: response.data };
  },

  // Get session settings for a team
  getSessionSettings: async (teamId: string): Promise<{ sessionDate: string; sessionTime: string } | null> => {
    const response = await api.get('/session-settings', { params: { teamId } });
    return response.data;
  },

  // Save session settings for a team
  saveSessionSettings: async (settings: { sessionDate: string; sessionTime: string; ratingsEnabled?: boolean }, teamId: string): Promise<ApiResponse<any>> => {
    const response = await api.put('/session-settings', { ...settings, teamId });
    return { success: true, data: response.data };
  },

  // Get past topics statistics
  getPastTopicsStats: async (teamId: string): Promise<{
    averageRating: number;
    totalCustomers: number;
    averageAttendees: number;
    totalTopics: number;
    topTopics: Topic[];
  }> => {
    const response = await api.get('/topics/past/stats', { params: { teamId } });
    return response.data;
  },

  // Get paginated past topics
  getPastTopics: async (teamId: string, sortBy: string = 'presentedAt', order: string = 'desc', limit: number = 50, lastKey?: string): Promise<{
    topics: Topic[];
    lastKey?: string;
    hasMore: boolean;
  }> => {
    const params: any = { teamId, sortBy, order, limit };
    if (lastKey) {
      params.lastKey = lastKey;
    }
    
    const response = await api.get('/topics/past', { params });
    
    console.log('getPastTopics - raw response:', response.data.topics.map((t: any) => ({ 
      id: t.id, 
      title: t.title, 
      presenters: t.presenters 
    })));
    
    return {
      topics: response.data.topics.map((topic: any) => ({
        topicId: topic.id,
        title: topic.title,
        description: topic.description,
        presenters: topic.presenters || [topic.submittedBy], // Fallback to submittedBy if no presenters
        recordingUrl: topic.recordingUrl,
        builderSpaceUrl: topic.builderSpaceUrl || '',
        gitlabUrl: topic.gitlabUrl || '',
        geoRegion: topic.geoRegion,
        sessionType: 'past',
        teamId: topic.teamId,
        teamName: topic.teamName,
        attendeeCount: topic.attendeeCount || 0,
        sessionCompleted: true,
        createdAt: topic.submittedAt,
        presentedAt: topic.presentedAt,
        duration: topic.duration,
        averageUsefulnessRating: topic.averageUsefulnessRating,
        totalCustomerConversations: topic.totalCustomerConversations,
        totalHoursSaved: topic.totalHoursSaved
      })),
      lastKey: response.data.lastKey,
      hasMore: response.data.hasMore
    };
  },

  // Deleted topics
  getDeletedTopics: async (teamId: string = 'all') => {
    const response = await api.get(`/topics/deleted?teamId=${teamId}`);
    return response.data;
  },

  restoreTopic: async (topicId: string) => {
    const response = await api.post(`/topics/${topicId}/restore`);
    return response.data;
  },

  permanentlyDeleteTopic: async (topicId: string) => {
    const response = await api.delete(`/topics/${topicId}/permanent`);
    return response.data;
  },

  // My Topic Stats API methods
  getMyTopics: async (limit: number = 50, lastKey?: string) => {
    const params = new URLSearchParams({ limit: limit.toString() });
    if (lastKey) {
      params.append('lastKey', lastKey);
    }
    const response = await api.get(`/topics/my-topics?${params}`);
    return response.data;
  },

  getMyStats: async () => {
    const response = await api.get('/topics/my-stats');
    return response.data;
  },

  getTopicRaters: async (topicId: string) => {
    const response = await api.get(`/topics/${topicId}/raters`);
    return response.data;
  },

  getMyImpactedColleagues: async () => {
    const response = await api.get('/topics/my-impacted-colleagues');
    return response.data;
  },

  // Series Info methods
  getSeriesInfo: async (teamId: string) => {
    const response = await api.get(`/series-info/${teamId}`);
    return response.data;
  },

  getSeriesInfoSource: async (teamId: string) => {
    const response = await api.get(`/series-info/${teamId}/source`);
    return response.data;
  },

  saveSeriesInfo: async (teamId: string, markdown: string) => {
    const response = await api.put(`/series-info/${teamId}`, { markdown });
    return response.data;
  },
};

export default api;
